'use client';

import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { usePathname } from 'next/navigation';
import { getSourcePath } from '@/rewrites';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/pro-duotone-svg-icons';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface HeaderNavigationProps {
  leftElements?: NavigationElementProps[];
  rightElements?: NavigationElementProps[];
}

interface NavigationElementProps {
  label: string;
  link?: string;
  handleClick?: (e: any) => void;
  icon?: IconDefinition;
  className?: string;
  menuItem?: NavigationElementProps[];
  hideIconMobile?: boolean;
  target?: React.HTMLAttributeAnchorTarget;
  component?: React.ReactNode;
  showSeparator?: boolean;
  accessiblePaths?: string[];
}

const isPathActive = (currentPath: string | null, itemPath: string | null, accessiblePaths?: string[]): boolean => {
  if (!currentPath || !itemPath) return false;

  const pathsToCheck = [itemPath];
  if (accessiblePaths) {
    pathsToCheck.push(...accessiblePaths);
  }

  for (const pathToCheck of pathsToCheck) {
    if (currentPath === pathToCheck) return true;

    if (currentPath.startsWith(pathToCheck + '/')) return true;
  }

  return false;
};

const NavigationItem: React.FC<{
  nav: NavigationElementProps;
  onNavigate?: () => void;
  isMobile?: boolean;
}> = ({ nav, onNavigate, isMobile }) => {
  const pathname = usePathname();

  const isSubmenuItemActive = nav.menuItem?.some((item) => {
    const itemPath = getSourcePath(item.link);
    return isPathActive(pathname, itemPath, item.accessiblePaths);
  });

  if (nav.menuItem) {
    if (isMobile) {
      return (
        <>
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full border border-primary text-base hover:cursor-default hover:bg-base-100 ${
              isSubmenuItemActive ? 'bg-primary text-base-100 hover:text-primary' : ''
            }`}
          >
            {nav.label}
          </span>
          <ul className="flex flex-col gap-1 p-2">
            {nav.menuItem.map((item) => (
              <li key={item.label}>
                <NavigationLink item={item} onClick={onNavigate} isMobile={true} />
              </li>
            ))}
          </ul>
        </>
      );
    }

    return (
      <details className="dropdown dropdown-end text-lg">
        <summary
          className={`flex h-9 w-9 items-center justify-center rounded-full border border-primary text-base font-normal after:content-none hover:bg-base-100 ${
            isSubmenuItemActive ? 'bg-primary text-base-100 hover:text-primary' : ''
          }`}
        >
          {nav.label}
        </summary>
        <ul className="dropdown-content !mt-0 flex min-w-52 flex-col gap-1 rounded-t-none bg-base-100 shadow-2xl">
          {nav.menuItem.map((item) => (
            <li key={item.label}>
              <NavigationLink item={item} onClick={onNavigate} />
            </li>
          ))}
        </ul>
      </details>
    );
  } else if (nav.component) {
    return <div className="relative flex items-center">{nav.component}</div>;
  }

  return (
    <NavigationLink item={nav} onClick={isMobile ? onNavigate : undefined} isMainNav={!isMobile} isMobile={isMobile} />
  );
};

const NavigationLink: React.FC<{
  item: NavigationElementProps;
  onClick?: () => void;
  isMainNav?: boolean;
  isMobile?: boolean;
}> = ({ item, onClick, isMainNav = false, isMobile = false }) => {
  const pathname = usePathname();
  const itemPath = getSourcePath(item.link);
  const isSelected = isPathActive(pathname, itemPath, item.accessiblePaths);

  let styleClass = '';
  if (isMainNav) {
    styleClass = 'rounded-lg px-3 text-lg font-normal';
  } else if (isMobile && item.icon && !item.hideIconMobile) {
    styleClass = 'px-0.5';
  } else {
    styleClass = 'px-2 py-1.5';
  }

  const baseClassName = `flex items-center gap-1 ${
    isSelected ? 'bg-primary text-base-100' : 'hover:text-primary'
  } ${styleClass} ${item.className || ''}`;

  if (item.handleClick) {
    return (
      <button
        className={baseClassName}
        onClick={(e) => {
          item.handleClick?.(e);
          onClick?.();
        }}
      >
        {item.icon && !(item.hideIconMobile && isMobile) && (
          <FontAwesomeIcon className="fa-fw h-4 w-4" icon={item.icon} />
        )}
        {item.label}
      </button>
    );
  }

  return (
    <Link href={item.link || '#'} onClick={onClick} target={item.target || '_self'} className={baseClassName}>
      {item.icon && <FontAwesomeIcon className="fa-fw h-4 w-4" icon={item.icon} />}
      {item.label}
    </Link>
  );
};

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({ leftElements, rightElements }) => {
  const [isMounted, setIsMounted] = useState(false);

  const isDesktopView = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      document.querySelectorAll<HTMLDetailsElement>('.dropdown').forEach((dropdown) => {
        dropdown.open = false;
      });
    };

    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const renderNavigationList = (elements?: NavigationElementProps[]) => (
    <ul className={`menu menu-horizontal gap-2 !p-0`}>
      {elements?.map((nav, index) => (
        <React.Fragment key={`${nav.label}-${index}`}>
          {nav.component ? (
            <div className="relative mx-4 flex items-center">
              {nav.component}
              {nav.showSeparator && (
                <div className="absolute -left-4 top-1/2 my-0 mr-8 box-content h-8 w-[1px] -translate-y-1/2 border-0 bg-separator p-0" />
              )}
            </div>
          ) : (
            !nav.component && (
              <li className="flex items-center">
                <NavigationItem nav={nav} />
                {nav.showSeparator && (
                  <div className="absolute -left-1 top-1/2 my-0 mr-8 box-content h-8 w-[1px] -translate-y-1/2 border-0 bg-separator p-0" />
                )}
              </li>
            )
          )}
        </React.Fragment>
      ))}
    </ul>
  );

  const MobileMenu = () => (
    <div className="navbar !min-h-0 !p-0 lg:hidden">
      <div className="flex w-full justify-end">
        <div className="flex items-center">
          {[...(leftElements || []), ...(rightElements || [])]
            .filter((nav) => nav.component)
            .map((nav, index) => (
              <div key={`${nav.label}-${index}`} className="relative mx-2 flex items-center">
                {nav.component}
              </div>
            ))}
          <div className="drawer drawer-end">
            <input id="drawer-toggle" type="checkbox" className="drawer-toggle" />
            <div className="drawer-content">
              <button className="btn btn-ghost" onClick={() => document.getElementById('drawer-toggle')?.click()}>
                <FontAwesomeIcon className="fa-fw h-5 w-5" icon={faBars} />
              </button>
            </div>
            <div className="drawer-side">
              <label htmlFor="drawer-toggle" className="drawer-overlay" />
              <div className="flex h-full max-w-xs flex-col bg-base-100 p-4 text-base-content max-sm:w-full sm:w-3/5">
                <div className="flex justify-end">
                  <button
                    className="btn btn-square btn-ghost"
                    onClick={() => document.getElementById('drawer-toggle')?.click()}
                  >
                    <FontAwesomeIcon icon={faTimes} className="fa-fw h-4 w-4" />
                  </button>
                </div>

                <ul className="menu gap-1 text-2xl">
                  {[...(leftElements || []), ...(rightElements || [])]
                    .filter((nav) => !nav.component)
                    .map((nav, index) => (
                      <li className="text-primary" key={`${nav.label}-${index}`}>
                        <NavigationItem
                          nav={nav}
                          onNavigate={() => document.getElementById('drawer-toggle')?.click()}
                          isMobile={true}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Early return during SSR or before mount to prevent hydration mismatch
  if (!isMounted) {
    return null;
  }

  return (
    <>
      {isDesktopView ? (
        <div className="navbar !min-h-0 w-full justify-between !p-0">
          {renderNavigationList(leftElements)}
          {renderNavigationList(rightElements)}
        </div>
      ) : (
        <MobileMenu />
      )}
    </>
  );
};

export default HeaderNavigation;
